import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Edit, Trash2, Plus } from 'lucide-react';
import apiService from '../services/api';
import CreateTeamMemberModal, { TeamMemberFormValues } from './CreateTeamMemberModal';

interface ModuleOption {
  id: number;
  name: string;
  displayName: string;
  description: string;
  category?: string;
}

interface CompanyOption {
  tenantId: number;
  name: string;
}

interface TeamMember {
  id: number;
  name: string;
  email: string;
  phone?: string;
  companyAccess?: { tenantId: number; canWrite: boolean }[] | null;
  permissions: { moduleId: number }[];
}

interface TeamMembersSectionProps {
  modules: ModuleOption[];
  companies: CompanyOption[];
}

const TeamMembersSection: React.FC<TeamMembersSectionProps> = ({ modules, companies }) => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<TeamMember | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['team-members'],
    queryFn: () => apiService.get('/admin/team-members'),
  });

  const createMutation = useMutation({
    mutationFn: (values: TeamMemberFormValues) => apiService.post('/admin/team-members', values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      setShowModal(false);
      toast.success('Team member created successfully');
    },
    onError: (error: any) => toast.error(error.response?.data?.message || 'Failed to create team member'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ memberId, values }: { memberId: number; values: TeamMemberFormValues }) =>
      apiService.put(`/admin/team-members/${memberId}`, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      setShowModal(false);
      setEditing(null);
      toast.success('Team member updated successfully');
    },
    onError: (error: any) => toast.error(error.response?.data?.message || 'Failed to update team member'),
  });

  const deleteMutation = useMutation({
    mutationFn: (memberId: number) => apiService.delete(`/admin/team-members/${memberId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      toast.success('Team member deleted successfully');
    },
    onError: (error: any) => toast.error(error.response?.data?.message || 'Failed to delete team member'),
  });

  const members: TeamMember[] = (data as any)?.teamMembers || [];
  const companyName = (tenantId: number) => companies.find((c) => c.tenantId === tenantId)?.name || `#${tenantId}`;

  const handleSubmit = (values: TeamMemberFormValues) => {
    if (editing) {
      updateMutation.mutate({ memberId: editing.id, values });
    } else {
      createMutation.mutate(values);
    }
  };

  return (
    <div className="mt-8">
      <div className="mb-4">
        <button
          onClick={() => { setEditing(null); setShowModal(true); }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Create Team Member
        </button>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Team Members</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Companies</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr><td colSpan={4} className="px-6 py-4 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </td></tr>
              ) : members.length === 0 ? (
                <tr><td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">No team members yet.</td></tr>
              ) : (
                members.map((m) => (
                  <tr key={m.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{m.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{m.email}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="flex flex-wrap gap-1">
                        {(m.companyAccess || []).map((g) => (
                          <span key={g.tenantId} className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${g.canWrite ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>
                            {companyName(g.tenantId)}{!g.canWrite && ' (read-only)'}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button onClick={() => { setEditing(m); setShowModal(true); }} className="text-primary-600 hover:text-primary-900">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => window.confirm('Delete this team member?') && deleteMutation.mutate(m.id)} className="text-red-600 hover:text-red-900">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <CreateTeamMemberModal
          modules={modules}
          companies={companies}
          initial={editing ? {
            name: editing.name,
            email: editing.email,
            phone: editing.phone || '',
            permissions: editing.permissions.map((p) => ({ moduleId: p.moduleId })),
            companyAccess: editing.companyAccess || [],
          } : null}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSubmit={handleSubmit}
          submitting={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </div>
  );
};

export default TeamMembersSection;
